import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";
import ApiService from "../../Services/apiService";
import AlertService from "../../Services/alertService";
import TranslationService from "../../Services/translationService";
import MainService from "../../Services/mainService";
import {
	addPartialViewSpinner,
	removePartialViewSpinner,
} from "../../Store/Actions/spinner";
import { ERROR_KEY } from "../../Constants/mainKeys";
import ArrowBackSvg from "../../Components/Svg/arrowBackSvg";
import TemplateDocumentView from "./components/TemplateDocumentView";

const isLikelyImageUrl = url => {
	if (!url || typeof url !== "string") return false;
	const lower = url.split("?")[0].toLowerCase();
	return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(lower);
};

const hasTemplateBodyData = doc => {
	if (!doc?.documentTemplateId) return false;
	if (!doc.data || typeof doc.data !== "string") return false;
	const s = doc.data.trim();
	if (!s || s === "null") return false;
	try {
		JSON.parse(s);
		return true;
	} catch {
		return false;
	}
};

function DocumentView(props) {
	const dispatch = useDispatch();
	const { language } = useSelector(state => state.language);
	const translations = useSelector(state => state.translation.translations);
	const [translationService, setTranslationService] = useState(null);
	const [doc, setDoc] = useState(null);
	const { documentId } = props.match.params;

	useEffect(() => {
		setTranslationService(new TranslationService(translations));
	}, [translations]);

	useEffect(() => {
		if (documentId) getDocument(documentId);
	}, [documentId]);

	const getDocument = id => {
		const spinnerId = uuid();
		dispatch(addPartialViewSpinner(spinnerId));
		ApiService.getDocumentById(id)
			.then(res => {
				if (res && res.data) setDoc(res.data);
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
			})
			.finally(() => {
				setTimeout(() => {
					dispatch(removePartialViewSpinner(spinnerId))
				}, 1000);
			});
	};

	const printImage = src => {
		const win = window.open("", "", "height=800,width=800");
		win.document.open();
		win.document.write(
			[
				"<html>",
				"  <head></head>",
				'  <body onload="window.print()" onafterprint="window.close()">',
				`    <img src="${src}" />`,
				"  </body>",
				"</html>",
			].join(""),
		);
		win.document.close();
	};

	const onPrint = () => {
		if (!doc) return;
		if (hasTemplateBodyData(doc)) {
			const root = document.querySelector(".document-styles");
			const container =
				root?.querySelector(".draw-template-table-container") ||
				root?.querySelector(".template-table-container");
			const isWideScrollable =
				Boolean(container) && container.scrollWidth > container.clientWidth + 2;

			let injectedStyle = null;
			if (root && isWideScrollable) {
				root.classList.add("print-wide-table");
				// Only for wide/scrollable tables (Journal-like): enforce landscape just for this print
				injectedStyle = document.createElement("style");
				injectedStyle.setAttribute("data-print-wide-table", "1");
				injectedStyle.media = "print";
				injectedStyle.textContent = "@page { size: A4 landscape; margin: 10mm; }";
				document.head.appendChild(injectedStyle);
			}

			const cleanup = () => {
				try {
					if (root) root.classList.remove("print-wide-table");
					if (injectedStyle) injectedStyle.remove();
				} catch {
					// ignore
				}
				window.removeEventListener("afterprint", cleanup);
			};
			window.addEventListener("afterprint", cleanup);

			window.print();
			return;
		}
		const src =
			doc.signed && doc.signFilePath ? doc.signFilePath : doc.filePath;
		if (src && isLikelyImageUrl(src)) {
			printImage(src);
		} else if (src) {
			window.open(src, "_blank");
		} else {
			window.print();
		}
	};

	if (!translationService) return null;

	const t = (key, fallback) => translationService.translate(key) || fallback;

	const showTemplate = hasTemplateBodyData(doc);
	const imageUrl =
		!showTemplate &&
		(doc?.signed && doc?.signFilePath ? doc.signFilePath : doc?.filePath);

	return (
		<div className="container mt-4 document-view-page">
			<div className="row">
				<div className="col-12">
					<h2 className="content-title p-0 d-flex align-items-center">
						<Link
							to={`/${language}/document`}
							title={t("TR_BACK", "Back")}
							className="mr-2"
							onClick={e => {
								e.preventDefault();
								window.history.back();
							}}>
							<ArrowBackSvg />
						</Link>
						<span className="mb-0">
							{doc?.name || t("TR_DOCUMENT_VIEW", "Document")}
						</span>
					</h2>
				</div>
			</div>
			<hr />

			{doc && (
				<>
					<div className="row justify-content-center">
						<div className="col-12 col-lg-10">
							<div className="document-form">
								<div className="document-styles">
									<div className="template-form-root template-container bg-white shadow-sm rounded p-4 mb-4">
										{showTemplate ? (
											<TemplateDocumentView
												templateId={doc.documentTemplateId}
												templateContent={doc.data}
											/>
										) : imageUrl && isLikelyImageUrl(imageUrl) ? (
											<div className="text-center">
												<img
													src={imageUrl}
													alt=""
													className="img-fluid"
													style={{ maxWidth: "100%" }}
												/>
											</div>
										) : imageUrl ? (
											<p className="mb-0">
												<a
													href={imageUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="mindalay--btn-default">
													{t("TR_OPEN_DOCUMENT", "Open document")}
												</a>
											</p>
										) : (
											<p className="text-muted mb-0">
												{t("TR_NO_PREVIEW", "No preview available.")}
											</p>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="row mt-2 mb-5 document-actions">
						<div className="col-12 d-flex justify-content-end flex-wrap">
							<button
								type="button"
								className="mindalay--btn-dark mr-2 mb-2 btn-sm"
								onClick={onPrint}>
								{t("TR_PRINT_OUT", "Print out")}
							</button>
							{
								doc?.filePath ?
									<a
										className="mindalay--btn-dark mindalay--btn-link btn-sm mb-2 d-inline-flex align-items-center"
										href={`${doc?.filePath}/download`}
										target="_blank"
										rel="noreferrer">
										{t("TR_DOWNLOAD", "Download")}
									</a>
									: null
							}
						</div>
					</div>
				</>
			)}
		</div>
	);
}

export default withRouter(DocumentView);
